// A crop is a square cut from a picture: x and y are its centre as fractions of
// the picture's width and height, size its side as a fraction of the width.
export type Crop = { x: number; y: number; size: number; flip: boolean };
// The same square in pixels of the displayed picture.
export type Box = { left: number; top: number; side: number };

export const MIN_SIZE = 0.05;
export const MAX_SIZE = 2;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function toBox(crop: Crop, width: number, height: number): Box {
	const side = crop.size * width;
	return { left: crop.x * width - side / 2, top: crop.y * height - side / 2, side };
}

// The size is clamped first and the centre follows the clamped box.
export function fromBox(box: Box, width: number, height: number, flip: boolean): Crop {
	const size = clamp(box.side / width, MIN_SIZE, MAX_SIZE);
	const side = size * width;
	return {
		x: clamp((box.left + side / 2) / width, 0, 1),
		y: clamp((box.top + side / 2) / height, 0, 1),
		size,
		flip,
	};
}

// A picture with no width or height yet cannot be dragged on: the moves leave the crop alone.
const measured = (width: number, height: number) => width > 0 && height > 0;

export function move(crop: Crop, dx: number, dy: number, width: number, height: number): Crop {
	if (!measured(width, height)) return crop;
	return { ...crop, x: clamp(crop.x + dx / width, 0, 1), y: clamp(crop.y + dy / height, 0, 1) };
}

// Grows the side by `grow` pixels, keeping the top-left corner fixed.
export function resize(crop: Crop, grow: number, width: number, height: number): Crop {
	if (!measured(width, height)) return crop;
	const box = toBox(crop, width, height);
	return fromBox({ ...box, side: box.side + grow }, width, height, crop.flip);
}

// Sets the size, keeping the centre.
export function withSize(crop: Crop, size: number): Crop {
	return { ...crop, size: clamp(size, MIN_SIZE, MAX_SIZE) };
}

// The picture as the background of a square preview of `edge` pixels, showing
// exactly the crop. `aspect` is the picture's height over its width.
export function previewStyle(crop: Crop, aspect: number, edge: number) {
	const scale = edge / crop.size;
	return {
		backgroundSize: `${scale}px ${scale * aspect}px`,
		backgroundPosition: `${-(crop.x - crop.size / 2) * scale}px ${-(crop.y * aspect - crop.size / 2) * scale}px`,
		transform: crop.flip ? "scaleX(-1)" : "none",
	};
}
